import { action, makeObservable, observable, runInAction } from 'mobx';
import { AppointmentType } from '@prisma/client';
import type { ParticipantNachversandResDto } from '@web/common';
import { setupMobX } from '../util/setupMobX';
import { getMyNachversand } from '../request/authenticated-requests/participant';
import { getNachversandBookingLink } from '../request/authenticated-requests/appointment';

/**
 * The participant's Part 2 Nachversand for the "Versand" tab and the home banner. Each screen owns
 * its own instance, so every visit starts from fresh data.
 */
export class NachversandStore {
  @observable public nachversand: ParticipantNachversandResDto | null = null;
  @observable public loadFailed = false;
  @observable public bookingLink: string | null = null;
  @observable public bookingLinkFailed = false;

  constructor() {
    makeObservable(this);
  }

  // a failed refresh keeps the data already shown; without data the banner renders nothing and
  // the tab shows an error with a retry
  @action public load = () => {
    this.loadFailed = false;
    getMyNachversand()
      .then((nachversand) => {
        runInAction(() => {
          this.nachversand = nachversand;
        });
        if (nachversand.eligible) {
          this.loadBookingLink();
        }
      })
      .catch(() => {
        runInAction(() => {
          this.loadFailed = true;
        });
      });
  };

  // the booking happens on cal.com in another tab, so reload when the participant comes back;
  // returns the cleanup for useEffect
  public loadAndReloadOnVisible = () => {
    const reloadIfVisible = () => {
      if (document.visibilityState === 'visible') {
        this.load();
      }
    };

    this.load();
    document.addEventListener('visibilitychange', reloadIfVisible);
    return () => {
      document.removeEventListener('visibilitychange', reloadIfVisible);
    };
  };

  @action private loadBookingLink = () => {
    getNachversandBookingLink(AppointmentType.NACHVERSAND_PART2)
      .then((response) => {
        runInAction(() => {
          this.bookingLink = response.url;
          this.bookingLinkFailed = false;
        });
      })
      .catch(() => {
        runInAction(() => {
          this.bookingLink = null;
          this.bookingLinkFailed = true;
        });
      });
  };
}

const { provider, useStore } = setupMobX<NachversandStore>();
export const useNachversandStore = useStore;
export const NachversandStoreProvider = provider;
